// src/app/routes/AppRoutes.tsx
import React, {FC, useEffect} from 'react'
import {BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation} from 'react-router-dom'
import {ErrorsPage} from '../modules/errors/ErrorsPage'
import {Logout, AuthPage} from '../modules/auth'
import {useReduxAuth} from '../modules/auth/useReduxAuth'
// import {useSubscriptionStatusMonitor} from '../modules/auth/useSubscriptionStatusMonitor' // Removed to avoid duplicate monitoring
import {App} from '../App'
import {UserRoutes} from './UserRoutes'
import {USER_ROLES} from '../constants/StatusConstants'
import { setNavigationCallback } from '../modules/auth/core/NavigationHelper'
// import { handleRouteChange } from '../services/api-client' // Temporarily disabled to fix refresh loop

const {PUBLIC_URL} = process.env

// Inner component that has access to navigate
const AppRoutesInner: FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const {user: currentUser, isAuthenticated: hasValidToken, isFetchingUser} = useReduxAuth()

  // Subscription status monitoring is now handled only in ReduxAuthProvider to avoid duplication
  // useSubscriptionStatusMonitor()

  // Set up navigation callback for use in places where React Router is not available
  useEffect(() => {
    setNavigationCallback((path: string) => {
      navigate(path, { replace: true })
    })
  }, [navigate])

  // Handle route changes and trigger /auth/me API call - TEMPORARILY DISABLED to fix refresh loop
  // useEffect(() => {
  //   handleRouteChange(location.pathname)
  // }, [location.pathname])

  // Handle navigation after authentication
  useEffect(() => {
    if (hasValidToken && currentUser && !isFetchingUser) {
      // If we're on the login page and user is authenticated, redirect to dashboard
      if (location.pathname === '/login' || location.pathname === '/') {
        navigate('/dashboard', { replace: true })
      }
    }
  }, [navigate, hasValidToken, currentUser, isFetchingUser, location.pathname])

  // Use token-based authentication first, then role-based when user data is available
  const isAuthenticated = hasValidToken && (
    // If we're still fetching user data, consider authenticated (prevent login redirect)
    isFetchingUser || 
    // If we have user data, check role
    (Boolean(currentUser) && [
      USER_ROLES.ADMIN,
      USER_ROLES.DOCTOR,
      USER_ROLES.NURSE,
      USER_ROLES.SUB_ADMIN,
      USER_ROLES.BILLING_PERSONNEL,
      USER_ROLES.MEDICAL_ASSISTANT,
    ].includes(currentUser!.role_id))
  )

  // if user try to access a protected route while not authenticated, save the intended URL
  useEffect(() => {
    if (!isAuthenticated && location.pathname !== '/login') {
      localStorage.setItem("intended_url", location.pathname)
    }
  }, [isAuthenticated, location.pathname])
  
  // additional check in middleware if staff or doctor is inactive
  const isActive = !currentUser || currentUser?.status === 1;

  return (
    <Routes>
      <Route element={<App />}>
        {/* Publicly available routes */}
        <Route path='error/*' element={<ErrorsPage />} />
        <Route path='logout' element={<Logout />} />

        {(isAuthenticated && isActive) ? (
          // Authenticated: show main app
          <>
            <Route path='/*' element={<UserRoutes />} />
            <Route index element={<Navigate to='/dashboard' replace />} />
          </>
        ) : (
          // Not authenticated: show login
          <>
            <Route path='/*' element={<AuthPage />} />
            <Route index element={<Navigate to='/login' replace />} />
          </>
        )}
      </Route>
    </Routes>
  )
}

const AppRoutes: FC = () => {
  return (
    <BrowserRouter basename={PUBLIC_URL}>
      <AppRoutesInner />
    </BrowserRouter>
  )
}

export {AppRoutes}
